import React, { useEffect, useState, useCallback } from 'react';
import DashboardShell from '@/components/DashboardShell';
import api from '@/api/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '@/components/ui/alert-dialog';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { MoreHorizontal, Search, Star, Trash2, Eye, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { getImageUrl, PLACEHOLDER_IMAGE } from '@/api/client';
import AdminTable from '@/components/AdminTable';

const STATUS_BADGE = {
  active:         { style: { backgroundColor: '#D1FAE5', color: '#065F46' } },
  draft:          { style: { backgroundColor: '#F3F4F6', color: '#374151' } },
  sold:           { style: { backgroundColor: '#EDE9FE', color: '#5B21B6' } },
  pending_review: { style: { backgroundColor: '#FEF3C7', color: '#92400E' } },
  rejected:       { style: { backgroundColor: '#FEE2E2', color: '#991B1B' } },
};

const STATUS_LABEL = {
  active: 'Active', draft: 'Draft', sold: 'Sold',
  pending_review: 'Pending Review', rejected: 'Rejected',
};

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatPrice = (p) => {
  if (!p && p !== 0) return '—';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(p);
};

export default function AllListings() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Reject dialog
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectNote, setRejectNote] = useState('');
  const [rejecting, setRejecting] = useState(false);

  // Refund dialog
  const [refundTarget, setRefundTarget] = useState(null);
  const [refunding, setRefunding] = useState(false);

  const fetchListings = useCallback((status) => {
    setLoading(true);
    const params = status === 'all' ? '' : `?status=${status}`;
    api.get(`/admin/listings${params}`)
      .then(r => setListings(r.data))
      .catch(() => toast.error('Failed to load listings'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchListings(statusFilter); }, [statusFilter, fetchListings]);

  const filtered = listings.filter(l =>
    !search ||
    l.title?.toLowerCase().includes(search.toLowerCase()) ||
    l.seller_name?.toLowerCase().includes(search.toLowerCase())
  );

  const toggleFeature = async (id, featured) => {
    try {
      await api.put(`/admin/listings/${id}/feature`);
      setListings(prev => prev.map(l => l.id === id ? { ...l, featured: !featured } : l));
      toast.success(featured ? 'Removed from featured' : 'Listing featured');
    } catch {
      toast.error('Failed to update feature status');
    }
  };

  const handleApprove = async (id) => {
    try {
      await api.put(`/admin/listings/${id}/approve`);
      toast.success('Listing approved and live');
      fetchListings(statusFilter);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to approve listing');
    }
  };

  const handleRejectConfirm = async () => {
    if (!rejectTarget) return;
    setRejecting(true);
    try {
      await api.put(`/admin/listings/${rejectTarget.id}/reject`, { admin_notes: rejectNote });
      toast.success('Listing rejected');
      setRejectTarget(null);
      setRejectNote('');
      fetchListings(statusFilter);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reject listing');
    } finally {
      setRejecting(false);
    }
  };

  const handleRefundConfirm = async () => {
    if (!refundTarget) return;
    setRefunding(true);
    try {
      const r = await api.post(`/admin/listings/${refundTarget.id}/refund`);
      toast.success(`Refund issued — £${r.data.amount?.toFixed(2) || ''}`);
      setRefundTarget(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Refund failed');
    } finally {
      setRefunding(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/listings/${deleteId}`);
      toast.success('Listing deleted');
      setDeleteId(null);
      fetchListings(statusFilter);
    } catch {
      toast.error('Failed to delete listing');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <DashboardShell title="All Listings">
      <div className="space-y-5">
        {/* Controls */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44" style={{ borderColor: 'var(--border-clr)', backgroundColor: 'var(--surface)' }}>
              <SelectValue placeholder="Filter status" />
            </SelectTrigger>
            <SelectContent style={{ backgroundColor: 'var(--surface)', borderColor: 'var(--border-clr)' }}>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending_review">Pending Review</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="sold">Sold</SelectItem>
            </SelectContent>
          </Select>
          <div className="relative w-full sm:w-60">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search title or seller…"
              className="pl-8 text-sm"
              style={{ borderColor: 'var(--border-clr)', backgroundColor: 'var(--surface)' }}
            />
          </div>
        </div>

        {/* Table */}
        <AdminTable
          data={filtered}
          loading={loading}
          dataTestId="admin-listings-table"
          emptyTitle="No listings found"
          emptySubtitle="Try adjusting your filters or search."
          columns={[
            {
              key: 'listing', label: 'Listing',
              render: (l) => {
                const img = l.images?.[0] ? getImageUrl(l.images[0]) : PLACEHOLDER_IMAGE;
                return (
                  <div className="flex items-center gap-3">
                    <img src={img} alt={l.title} className="w-10 h-10 rounded-lg object-cover flex-shrink-0" style={{ backgroundColor: 'var(--bg-subtle)' }} />
                    <div className="min-w-0">
                      <p className="font-medium truncate" style={{ color: 'var(--text)' }}>{l.title}</p>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{l.category_name}</p>
                    </div>
                    {l.featured && <Star size={13} fill="currentColor" style={{ color: 'var(--gold)', flexShrink: 0 }} />}
                  </div>
                );
              },
            },
            { key: 'seller', label: 'Seller', render: (l) => <span style={{ color: 'var(--text-muted)' }}>{l.seller_name}</span> },
            { key: 'price', label: 'Price', render: (l) => <span style={{ color: 'var(--text)' }}>{formatPrice(l.price)}</span> },
            { key: 'status', label: 'Status', render: (l) => {
              const badge = STATUS_BADGE[l.status] || STATUS_BADGE.draft;
              const label = STATUS_LABEL[l.status] || l.status;
              return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium" style={badge.style}>{label}</span>;
            } },
            {
              key: 'actions', label: '', width: '60px',
              render: (l) => (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`listing-actions-${l.id}`}>
                      <MoreHorizontal size={15} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" style={{ backgroundColor: 'var(--surface)', borderColor: 'var(--border-clr)' }}>
                    <DropdownMenuItem asChild className="gap-2 cursor-pointer">
                      <a href={`/listings/${l.id}`} target="_blank" rel="noreferrer"><Eye size={14} /> View listing</a>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => toggleFeature(l.id, l.featured)} className="gap-2 cursor-pointer">
                      <Star size={14} /> {l.featured ? 'Remove featured' : 'Feature listing'}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {l.status === 'pending_review' && (
                      <>
                        <DropdownMenuItem
                          onClick={() => handleApprove(l.id)}
                          className="gap-2 cursor-pointer"
                          style={{ color: '#065F46' }}
                          data-testid={`approve-listing-${l.id}`}
                        >
                          <CheckCircle size={14} /> Approve listing
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => { setRejectTarget(l); setRejectNote(''); }}
                          className="gap-2 cursor-pointer"
                          style={{ color: 'var(--danger)' }}
                          data-testid={`reject-listing-${l.id}`}
                        >
                          <XCircle size={14} /> Reject listing
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                      </>
                    )}
                    {l.status === 'active' && (
                      <>
                        <DropdownMenuItem
                          onClick={() => { setRejectTarget(l); setRejectNote(''); }}
                          className="gap-2 cursor-pointer"
                          style={{ color: 'var(--danger)' }}
                        >
                          <XCircle size={14} /> Reject & unpublish
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                      </>
                    )}
                    <DropdownMenuItem
                      onClick={() => setRefundTarget(l)}
                      className="gap-2 cursor-pointer"
                      style={{ color: '#7C3AED' }}
                      data-testid={`refund-listing-${l.id}`}
                    >
                      <RefreshCw size={14} /> Issue refund
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => setDeleteId(l.id)}
                      className="gap-2 cursor-pointer"
                      style={{ color: 'var(--danger)' }}
                    >
                      <Trash2 size={14} /> Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ),
            },
          ]}
        />
      </div>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent style={{ backgroundColor: 'var(--surface)', borderColor: 'var(--border-clr)' }}>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ fontFamily: 'var(--font-serif)', color: 'var(--text)' }}>Delete listing?</AlertDialogTitle>
            <AlertDialogDescription style={{ color: 'var(--text-muted)' }}>
              This action cannot be undone. The listing will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel style={{ borderRadius: '10px' }}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              style={{ backgroundColor: 'var(--danger)', color: 'white', borderRadius: '10px' }}
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reject Dialog */}
      <Dialog open={!!rejectTarget} onOpenChange={() => setRejectTarget(null)}>
        <DialogContent style={{ backgroundColor: 'var(--surface)', borderColor: 'var(--border-clr)' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-serif)', color: 'var(--text)' }}>
              Reject listing
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            Rejecting <strong style={{ color: 'var(--text)' }}>{rejectTarget?.title}</strong>. The member will be notified by email.
          </p>
          <div>
            <label className="text-xs font-medium block mb-1.5" style={{ color: 'var(--text-muted)' }}>Note to member (optional)</label>
            <Textarea
              value={rejectNote}
              onChange={e => setRejectNote(e.target.value)}
              placeholder="Reason for rejection…"
              rows={3}
              style={{ borderColor: 'var(--border-clr)' }}
              data-testid="reject-note-input"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRejectTarget(null)} style={{ borderRadius: '10px' }}>Cancel</Button>
            <Button
              onClick={handleRejectConfirm}
              disabled={rejecting}
              style={{ backgroundColor: 'var(--danger)', color: 'white', borderRadius: '10px' }}
              data-testid="confirm-reject-btn"
            >
              {rejecting ? 'Rejecting…' : 'Reject Listing'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refund Dialog */}
      <AlertDialog open={!!refundTarget} onOpenChange={() => setRefundTarget(null)}>
        <AlertDialogContent style={{ backgroundColor: 'var(--surface)', borderColor: 'var(--border-clr)' }}>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ fontFamily: 'var(--font-serif)', color: 'var(--text)' }}>Issue refund?</AlertDialogTitle>
            <AlertDialogDescription style={{ color: 'var(--text-muted)' }}>
              This will issue a full refund for the listing fee paid for <strong>{refundTarget?.title}</strong>. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel style={{ borderRadius: '10px' }}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRefundConfirm}
              disabled={refunding}
              style={{ backgroundColor: '#7C3AED', color: 'white', borderRadius: '10px' }}
              data-testid="confirm-refund-btn"
            >
              {refunding ? 'Processing…' : 'Issue Refund'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardShell>
  );
}
